import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import PlatformSettingsLayout from '@/components/settings/platform/PlatformSettingsLayoutV2';
import { Panel } from '@ampm-7en-ai/ui/components/Panel';
import { ListCard, ListRow, ListRowSkeleton } from '@ampm-7en-ai/ui/components/ListRow';
import { EmptyState } from '@ampm-7en-ai/ui/components/EmptyState';
import { Button } from '@ampm-7en-ai/ui/components/Button';
import { Pill } from '@ampm-7en-ai/ui/components/StatusBadge';
import { IconChip } from '@ampm-7en-ai/ui/components/IconChip';
import { Callout } from '@ampm-7en-ai/ui/components/Callout';
import { ConfirmDialog } from '@ampm-7en-ai/ui/components/ConfirmDialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { API_ENDPOINTS, getApiUrl, getAuthHeaders, getRoleEndpoint } from '@/utils/api-config';
import type { Permission } from '@/context/PermissionContext';

/**
 * Who can do what: the roles a superadmin hands out, and what each one carries.
 *
 * Deleting a role is the one destructive thing on this page, so it asks in a
 * dialog that names the role rather than the browser's own confirm box.
 */

export interface Role {
  id: number;
  name: string;
  description: string;
  level: number;
  features: {
    id: number;
    name: string;
    permissions: Permission[];
  }[];
  is_active: boolean;
  created_at: string;
}

interface RolesResponse {
  message: string;
  data: Role[];
  status: string;
  features: string[] | null;
}

/** Every permission a role carries, named once, however many features grant it. */
const permissionsOf = (role: Role): string[] =>
  Array.from(
    new Set(
      (role.features ?? [])
        .flatMap((feature) => feature.permissions ?? [])
        .map((permission) => permission.name)
        .filter((name): name is string => Boolean(name))
    )
  );

/** The first few, with a count standing in for the rest. */
function Permissions({ role }: { role: Role }) {
  const all = permissionsOf(role);
  if (all.length === 0) return <span className="text-ui-xs text-ui-fg-subtle">No permissions yet</span>;

  const shown = all.slice(0, 4);
  const rest = all.length - shown.length;

  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {shown.map((name) => (
        <Pill key={name}>{name}</Pill>
      ))}
      {rest > 0 && <span className="text-ui-xs text-ui-fg-subtle">+{rest} more</span>}
    </span>
  );
}

export default function SecuritySettingsV2() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [roles, setRoles] = React.useState<Role[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [failed, setFailed] = React.useState<string | null>(null);
  const [removing, setRemoving] = React.useState<Role | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setFailed(null);
    try {
      const token = getToken();
      if (!token) throw new Error('You need to be signed in to see roles.');

      const response = await fetch(getApiUrl(API_ENDPOINTS.USER_ROLE), { headers: getAuthHeaders(token) });
      if (!response.ok) throw new Error('Couldn’t load the roles.');

      const body: RolesResponse = await response.json();
      setRoles(body.data ?? []);
    } catch (error) {
      setFailed(error instanceof Error ? error.message : 'Couldn’t load the roles.');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const remove = async () => {
    if (!removing) return;
    setDeleting(true);
    try {
      const token = getToken();
      if (!token) throw new Error('You need to be signed in to delete a role.');

      const response = await fetch(getApiUrl(getRoleEndpoint(removing.id)), {
        method: 'DELETE',
        headers: getAuthHeaders(token),
      });

      const body = await response.json().catch(() => null);
      if (!response.ok) throw new Error(body?.error?.message || 'The role wasn’t deleted.');

      toast({ title: body?.message || `${removing.name} deleted` });
      setRemoving(null);
      await load();
    } catch (error) {
      toast({
        title: 'That didn’t work',
        description: error instanceof Error ? error.message : 'The role wasn’t deleted.',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <PlatformSettingsLayout title="Security" description="The roles people can hold, and what each one allows.">
      {failed && (
        <Callout tone="danger" className="mb-4" title="Couldn’t load the roles">
          {failed}{' '}
          <button type="button" onClick={() => void load()} className="underline underline-offset-2">
            Try again
          </button>
        </Callout>
      )}

      <div className="mb-3 flex items-end justify-between gap-3">
        <div>
          <h2 className="text-ui-lg font-normal text-ui-fg">Roles</h2>
          <p className="mt-0.5 text-ui-sm text-ui-fg-muted">
            Each role is a set of permissions. People get everything their role carries.
          </p>
        </div>
        <Button variant="primary" onClick={() => navigate('/v2/settings/platform/security/role')}>
          <Plus /> New role
        </Button>
      </div>

      {loading ? (
        <ListCard>
          {Array.from({ length: 4 }, (_, index) => (
            <li key={index}>
              <ListRowSkeleton />
            </li>
          ))}
        </ListCard>
      ) : roles.length === 0 ? (
        <Panel variant="dashed">
          <EmptyState
            icon={ShieldCheck}
            title="No roles yet"
            description="A role groups permissions together so you can hand them out in one go."
            action={
              <Button variant="secondary" onClick={() => navigate('/v2/settings/platform/security/role')}>
                Create the first one
              </Button>
            }
          />
        </Panel>
      ) : (
        <ListCard>
          {roles.map((role) => (
            <li key={role.id}>
              <ListRow
                leading={<IconChip icon={ShieldCheck} tone={role.is_active ? 'accent' : 'neutral'} />}
                title={<span className="truncate">{role.name}</span>}
                description={
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    {role.description && <span className="text-ui-xs text-ui-fg-subtle">{role.description}</span>}
                    <span className="text-ui-xs text-ui-fg-subtle">Level {role.level}</span>
                  </span>
                }
                footer={<Permissions role={role} />}
                meta={!role.is_active ? <Pill>Inactive</Pill> : undefined}
                onClick={() => navigate(`/v2/settings/platform/security/role/${role.id}`)}
                trailing={
                  <span className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      aria-label={`Edit ${role.name}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(`/v2/settings/platform/security/role/${role.id}`);
                      }}
                    >
                      <Pencil />
                    </Button>
                    <Button
                      variant="ghost"
                      aria-label={`Delete ${role.name}`}
                      className="text-ui-danger"
                      onClick={(event) => {
                        event.stopPropagation();
                        setRemoving(role);
                      }}
                    >
                      <Trash2 />
                    </Button>
                  </span>
                }
              />
            </li>
          ))}
        </ListCard>
      )}

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && !deleting && setRemoving(null)}
        title={`Delete ${removing?.name ?? 'this role'}?`}
        description={
          removing && permissionsOf(removing).length > 0
            ? `Anyone holding it loses the ${permissionsOf(removing).length} permissions it carries. This can’t be undone.`
            : 'Anyone holding it loses it. This can’t be undone.'
        }
        confirmLabel={deleting ? 'Deleting…' : 'Delete role'}
        tone="danger"
        onConfirm={remove}
      />
    </PlatformSettingsLayout>
  );
}
